/**
 * Email-safe TinyMCE `valid_elements` whitelist shared by every campaign editor
 * (email steps, LinkedIn steps, AI-snippet prompts, sender signatures).
 *
 * Images are intentionally **excluded**. There is no working image-upload
 * endpoint, so a pasted image becomes an inline base64 `data:` URI. Those URIs:
 *   1. bloat `campaignConfig` (it carries every step + sender signature) past the
 *      API body-parser limit, producing a 413 PayloadTooLargeError on save; and
 *   2. are routinely stripped/blocked by email clients (Gmail/Outlook) anyway, so
 *      they rarely render for the recipient.
 *
 * Editors that use this constant also set `paste_data_images: false` so base64
 * image data can never be pasted in the first place; dropping `img` from the
 * whitelist is the belt-and-suspenders that strips any `<img>` that still slips
 * through (e.g. pasted HTML referencing a remote image).
 */
export const EMAIL_SAFE_VALID_ELEMENTS =
	"h1[style],h2[style],h3[style],p[style],span[style],br,strong/b,em/i,u,a[href|target|title],ul,ol,li," +
	"table[border|cellpadding|cellspacing|width|style],tr,td[colspan|rowspan|width|style]," +
	"tbody,thead,tfoot,hr,font[size|face]";

/**
 * Allowlist for the sender **signature** editor (PREC-4235).
 *
 * A signature is pasted, not written: WiseStamp, HubSpot, Outlook and every other
 * generator emit nested tables whose logo, photo and social icons are `<img>` tags
 * pointing at a hosted `https://` URL. Stripping `img` here removed all of them and
 * left empty cells and an invisible, icon-less LinkedIn link. So this list extends
 * {@link EMAIL_SAFE_VALID_ELEMENTS} with `img` and the table/link attributes those
 * generators rely on.
 *
 * The 413 risk the shared list guards against is a base64 `data:` image, not a
 * hosted one. The signature editor keeps `paste_data_images: false` and removes any
 * `data:` image that still arrives with `dropDataUriImages`, so only URLs are stored.
 */
export const SIGNATURE_VALID_ELEMENTS =
	"h1[style],h2[style],h3[style],p[style|align],div[style|align],span[style],br,strong/b,em/i,u," +
	"a[href|target|title|style|rel],ul,ol,li," +
	"img[src|alt|title|width|height|style|border|align]," +
	"table[border|cellpadding|cellspacing|width|height|style|align|bgcolor]," +
	"tr[style|align|valign|bgcolor]," +
	"td[colspan|rowspan|width|height|style|align|valign|bgcolor|nowrap]," +
	"th[colspan|rowspan|width|height|style|align|valign|bgcolor|nowrap]," +
	"tbody,thead,tfoot,hr[style],font[size|face|color]";
